/**
 * mvq-cart-resume.js
 *
 * Phase 2 of the two-phase add-to-cart. Cart page only.
 *
 * multi-variant-atc.js splits a customised multi-size add in two so the
 * artwork is uploaded exactly once:
 *
 *   POST 1  single-item /cart/add — first garment + the only copy of each file,
 *           carrying `_mvq_batch: <token>`, with return_to=/cart?mvq_resume=<token>
 *   ── this page ──
 *   POST 2  items[] /cart/add — the remaining sizes, each stamped with the
 *           hosted URL Shopify assigned to those files, plus the fee lines
 *
 * Both are native form submissions, i.e. top-level document requests. That is
 * the whole point: a scripted cart request landing moments after another cart
 * request is what draws Shopify's Cloudflare challenge, and a challenge answers
 * text/html, which a fetch can never satisfy. This file therefore makes **no
 * network requests at all** — the hosted URLs are read out of the
 * server-rendered #mvq-cart-file-props block (see sections/main-cart-items.liquid).
 *
 * Writing a hosted file URL to a line item as a plain string is long-standing
 * behaviour here: it's how gallery designs and shared designs reach the cart
 * (snippets/design-placement-block.liquid).
 */

(function () {
  'use strict';

  const PENDING_KEY  = 'mvq_pending_add';
  const BATCH_PROP   = '_mvq_batch';
  const RESUME_PARAM = 'mvq_resume';
  const HOOK_ID      = 'mvq-cart-file-props';

  // ── Pure helpers (exported for the node tests) ─────────────────────────────

  // Pull the pending payload, rejecting anything we can't trust: wrong shape,
  // wrong version, already consumed, or a token that doesn't match the one the
  // URL brought us. The token gate is what stops a later visit to /cart from
  // silently mutating someone's cart.
  function readPending(raw, urlToken) {
    let payload;
    try { payload = JSON.parse(raw); } catch (e) { return null; }
    if (!payload || payload.v !== 1) return null;
    if (!payload.token || !Array.isArray(payload.items)) return null;
    if (payload.consumed) return null;
    if (urlToken && payload.token !== urlToken) return null;
    return payload;
  }

  // Auto-resume requires the token that phase 1's redirect put in the URL.
  // Without it we could be on a manual /cart visit, a refresh, or a back-button
  // — none of which may add to someone's cart unprompted. readPending on its own
  // treats a null token as "don't check", which is right for the notice path but
  // must never be the gate for an automatic submit.
  function shouldAutoResume(raw, urlToken) {
    if (!urlToken) return null;
    return readPending(raw, urlToken);
  }

  // Find the hosted URL for each wanted property key, from the line phase 1
  // created. Matching on the batch token rather than the variant id matters:
  // the cart may already hold older lines with a File Upload property from a
  // previous add, and stamping their URL onto these sizes would attach the
  // wrong artwork.
  function findHostedUrls(lines, token, fileKeys) {
    const out = {};
    const line = (lines || []).find(l => l && l.properties && l.properties[BATCH_PROP] === token);
    if (!line) return out;
    (fileKeys || []).forEach(key => {
      const value = line.properties[key];
      // Only a hosted file counts. A key whose value never became a Shopify
      // file URL is left unset rather than stamped as a bogus string.
      if (typeof value === 'string' && value.indexOf('/uploads/') !== -1) out[key] = value;
    });
    return out;
  }

  // Stamp the hosted URLs onto every line that carries properties. Fee lines
  // have none and are passed through untouched, so they never gain a spurious
  // artwork property.
  function stampHostedUrls(items, hostedUrls) {
    return (items || []).map(item => {
      if (!item || !item.properties) return item;
      const properties = Object.assign({}, item.properties);
      Object.keys(hostedUrls || {}).forEach(key => { properties[key] = hostedUrls[key]; });
      return Object.assign({}, item, { properties: properties });
    });
  }

  // Exported for the node unit tests; a no-op in the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { readPending, shouldAutoResume, findHostedUrls, stampHostedUrls };
  }

  // ── Page gate ──────────────────────────────────────────────────────────────
  // Same suffix match as cart-fee-watcher.js, so locale prefixes (/en-gb/cart)
  // still count. Nothing to resume anywhere else. Deliberately below the export
  // seam so the pure helpers stay testable without stubbing a location.
  if (typeof document === 'undefined' || !window.MVQCartForm) return;
  const _path = (window.location?.pathname || '').replace(/\/+$/, '');
  if (!/(^|\/)cart$/.test(_path)) return;

  // ── DOM / storage glue ─────────────────────────────────────────────────────

  function readHook() {
    const el = document.getElementById(HOOK_ID);
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function rawPending() {
    try { return sessionStorage.getItem(PENDING_KEY); } catch (e) { return null; }
  }

  // Mark consumed rather than deleting, so a refresh can't replay the add but
  // the notice below can still offer to finish it if this submit never lands.
  function markConsumed(payload) {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(
        Object.assign({}, payload, { consumed: true })
      ));
    } catch (e) { /* storage gone; the token strip below still guards a refresh */ }
  }

  function stripResumeParam() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has(RESUME_PARAM)) return;
      url.searchParams.delete(RESUME_PARAM);
      window.history.replaceState(null, '', url.toString());
    } catch (e) { /* non-fatal */ }
  }

  function submitPhaseTwo(payload, hostedUrls) {
    markConsumed(payload);
    stripResumeParam();
    window.MVQCartForm.submitCartForm({
      items:    stampHostedUrls(payload.items, hostedUrls),
      returnTo: '/cart',
    });
  }

  // The customer is mid-order with only their first size in the cart. Never
  // leave that state unexplained — offer to finish it. Reuses the visual
  // language of cart-fee-watcher.js's rate-limit banner.
  function showResumeNotice(payload, hostedUrls) {
    if (document.getElementById('mvq-resume-banner')) return;
    // Without the hosted URLs, finishing the add would give the remaining sizes
    // no artwork — an order production can't fulfil. Explain instead of
    // offering a button that produces a broken order.
    const canFinish = Object.keys(hostedUrls || {}).length > 0;

    const target = document.querySelector('cart-items .page-width') ||
                   document.querySelector('cart-items') ||
                   document.body;

    const banner = document.createElement('div');
    banner.id = 'mvq-resume-banner';
    banner.setAttribute('role', 'alert');
    banner.style.cssText =
      'background:#fff3cd;border:1px solid #ffc107;color:#664d03;' +
      'padding:1.2rem 1.5rem;margin:1rem 0;border-radius:4px;' +
      'font-size:1.4rem;text-align:center;';

    const text = document.createElement('span');
    text.innerHTML = canFinish
      ? '<strong>Your other sizes haven’t been added yet.</strong><br>' +
        'Your design is saved against the item below. '
      : '<strong>Your other sizes haven’t been added yet.</strong><br>' +
        'Please add the remaining sizes from the product page again — your ' +
        'design is already saved against the item below.';
    banner.appendChild(text);

    if (canFinish) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = 'Add the remaining sizes';
      btn.style.cssText =
        'margin-left:1rem;padding:0.4rem 1rem;background:#664d03;color:#fff;' +
        'border:none;border-radius:3px;cursor:pointer;font-size:1.3rem;';
      btn.addEventListener('click', () => {
        btn.disabled = true;
        submitPhaseTwo(payload, hostedUrls);
      });
      banner.appendChild(btn);
    }

    target.insertBefore(banner, target.firstChild);
    banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function run() {
    const raw = rawPending();
    if (!raw) return;

    let urlToken = null;
    try { urlToken = new URL(window.location.href).searchParams.get(RESUME_PARAM); } catch (e) {}

    // Without a matching token we're not on the hop out of phase 1. Fall back
    // to the notice using whatever we can still resolve, rather than adding to
    // the cart unbidden.
    const payload = shouldAutoResume(raw, urlToken);
    if (!payload) {
      const unGated = readPending(raw, null);
      if (unGated) {
        const urls = findHostedUrls(readHook(), unGated.token, unGated.fileKeys);
        if (Object.keys(urls).length) showResumeNotice(unGated, urls);
      }
      return;
    }

    const hostedUrls = findHostedUrls(readHook(), payload.token, payload.fileKeys);
    if (!Object.keys(hostedUrls).length) {
      // Phase 1's line isn't on the page, or its files never became hosted
      // URLs. Adding the other sizes now would give them no artwork, so don't.
      console.warn('[MVQ-resume] No hosted file URLs found for batch', payload.token);
      showResumeNotice(payload, hostedUrls);
      return;
    }

    submitPhaseTwo(payload, hostedUrls);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
